import React, { useEffect } from 'react';
import ProcessingModal from '@/components/dashboard-modal/ProcessingModal';
import { usePageGeneration } from '@/hooks/content-generator/pages/use-page-generation';
import { PageGenerationRequest } from '@/types/page-generation-types';

interface PageGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  requestData: PageGenerationRequest | null;
  selectedResearch: any[];
  summaryData: Record<string, string>;
  onSuccess: (pageId: string) => void;
  workbookId?: string; // Optional for private pages
}

const PageGenerationModal: React.FC<PageGenerationModalProps> = ({
  isOpen,
  onClose,
  requestData,
  selectedResearch,
  summaryData,
  onSuccess,
  workbookId,
}) => {
  const { state, startGeneration, reset } = usePageGeneration(workbookId || '');

  useEffect(() => {
    if (isOpen && requestData && state.status === 'idle') {
      startGeneration(requestData, selectedResearch, summaryData);
    }
  }, [isOpen, requestData, startGeneration, state.status, selectedResearch, summaryData]);

  const handleClose = () => {
    reset();
    onClose();
  };
  
  const handleRetry = () => {
    if (requestData) {
      startGeneration(requestData, selectedResearch, summaryData);
    }
  };
  
  const handleSuccess = () => {
    if(state.generatedPageId) {
      onSuccess(state.generatedPageId);
    }
    handleClose();
  };

  // Calculate progress message based on current task
  const getProgressMessage = () => {
    const totalResearchCount = selectedResearch.length;
    const answeredQuestionsCount = requestData?.clarifyingQuestions?.length || 0;
    
    let contextMessage = '';
    if (totalResearchCount > 0 && answeredQuestionsCount > 0) {
      contextMessage = ` with ${totalResearchCount} research context${totalResearchCount > 1 ? 's' : ''} and ${answeredQuestionsCount} clarifying answer${answeredQuestionsCount > 1 ? 's' : ''}`;
    } else if (totalResearchCount > 0) {
      contextMessage = ` with ${totalResearchCount} research context${totalResearchCount > 1 ? 's' : ''}`;
    } else if (answeredQuestionsCount > 0) {
      contextMessage = ` with ${answeredQuestionsCount} clarifying answer${answeredQuestionsCount > 1 ? 's' : ''}`;
    }

    return `Your new document "${requestData?.title}" has been created${contextMessage}!`;
  };

  return (
    <ProcessingModal
      isOpen={isOpen}
      onClose={handleClose}
      state={state.status === 'processing' ? 'processing' : state.status === 'success' ? 'success' : 'error'}
      tasks={state.tasks}
      progress={state.progress}
      currentTaskDescription={state.currentTaskDescription}
      errorMessage={state.error || ''}
      onRetry={handleRetry}
      successMessage={getProgressMessage()}
      successActionText="Continue"
      onSuccessAction={handleSuccess}
      title="✨ Creating Your Document"
      description="Our AI is crafting intelligent content based on your requirements and context."
    />
  );
};

export default PageGenerationModal; 